import { Container } from 'inversify';
import {inversifyContainer} from '@gongxigames/framework/core/installer/inversify.config';
import {
    ComponentConfig, ComponentInstanceWithConfig, ComponentModuleConfig, ComponentModuleViewConfig,
    IModule,
    ISystemComponent, IView
} from '@gongxigames/framework/core/types';

type ModulesComponents = {
    modules: ComponentInstanceWithConfig<IModule>[];
    views: ComponentInstanceWithConfig<IView, ComponentModuleViewConfig>[];
};

type GenericConstructor<T> = new (...args: any[]) => T;

export class DependencyInjector {
    private container: Container;

    constructor() {
        this.container = inversifyContainer;
    }

    public getComponent<T>(id: symbol | string): T {
        return this.container.get(id);
    }

    public bindConstant<T>(id: symbol | string, value: T): void {
        this.container.bind(id).toConstantValue(value);
    }

    public bindSingleton<T>(id: symbol | string, constructor: GenericConstructor<T>): void {
        this.container.bind(id).to(constructor).inSingletonScope();
    }

    public bindClass<T>(id: symbol | string, constructor: GenericConstructor<T>): void {
        this.container.bind(id).to(constructor);
    }

    public isBound(id: symbol | string): boolean {
        return this.container.isBound(id);
    }

    public unbindAll(): void {
        this.container.unbindAll();
    }

    public bindModulesComponents(configs: {modules: ComponentModuleConfig[]; views: ComponentModuleViewConfig[];}): ModulesComponents {
        const modules = this.bindComponents<IModule, ComponentModuleConfig>(configs.modules);
        const views = this.bindComponents<IView, ComponentModuleViewConfig>(configs.views) ;

        return {modules, views};
    }

    public bindComponents<I extends ISystemComponent, C extends ComponentConfig>(componentsConfig: C[]): ComponentInstanceWithConfig<I, C>[] {
        const components = [];

        for (const config of componentsConfig) {
            if (config) {
                this.bindSingleton(config.NAME, config);

                const instance: I = this.getComponent<I>(config.NAME);

                components.push({instance, config});
            }
        }

        return components as ComponentInstanceWithConfig<I, C>[];
    }
}
